/**
 * D10 « Étiquettes des lots » — the pure state of the label dialog (design/ux/02_achats.md §6.7;
 * refonte WP-26; D-15, D-16, OP-ACH-10, OP-ACH-13).
 *
 * Input: the server payload (`GET /achats/:id/etiquettes` or `GET /etiquettes-lots`): lots with
 * their code, product text, lot PPA and default copies, the pharmacy name, the label settings.
 * State: the chosen format, « nom de la pharmacie », the included lots (the grid selection), the
 * copies per lot and an optional one-lot filter (the 🏷 of a line). Output: the LabelSheet input,
 * the totals of the footer, the reason printing is refused, the print-audit body.
 *
 * Lots without a code are never included (M-S3-22); copies stay within ETIQUETTE_COPIES (M-S3-21);
 * a default above the bound starts at the bound.
 */
import { ETIQUETTE_COPIES, PRINT_MESSAGES, etiquetteFormat } from "@/config/print.config.js";
import { normalizeCopies } from "@/lib/labels.js";

/** Where the dialog was opened from — the server's SOURCE_ETIQUETTES. */
export const SOURCE_ETIQUETTES = Object.freeze({ FACTURE: "FACTURE", LOTS: "LOTS" });

const borne = (n) => Math.min(ETIQUETTE_COPIES.MAX, Math.max(ETIQUETTE_COPIES.MIN, Math.trunc(Number(n) || 0)));

/**
 * The first state from a payload.
 * @param {object} donnees  server payload
 * @param {{ lotId?: string|null }} [opts]  open on one lot only (list pre-filtered)
 */
export function etatInitial(donnees, { lotId = null } = {}) {
  const lots = Array.isArray(donnees?.lots) ? donnees.lots : [];
  const p = donnees?.parametres ?? {};
  const filtre = lotId && lots.some((l) => l.lot_id === lotId) ? lotId : null;
  const imprimables = lots.filter((l) => l.imprimable && l.code);
  return {
    format: etiquetteFormat(p.etiquette_format).cle,
    nomPharmacie: p.etiquette_nom_pharmacie !== false,
    prix: p.etiquette_prix !== false,
    filtre,
    inclus: imprimables.filter((l) => !filtre || l.lot_id === filtre).map((l) => l.lot_id),
    copies: Object.fromEntries(lots.map((l) => [l.lot_id, borne(l.copies_defaut)])),
    imprimees: Object.fromEntries(lots.map((l) => [l.lot_id, l.etiquettes_imprimees ?? null])),
  };
}

/** The lots the list shows (all, or the one lot of a 🏷 opening). */
export const lotsVisibles = (lots, etat) => (etat.filtre ? lots.filter((l) => l.lot_id === etat.filtre) : lots);

/** A lot that can be printed (has its code). */
export const estImprimable = (lot) => Boolean(lot?.imprimable && lot?.code);

/** The grid selection (only printable, visible lots ever count). */
export function avecInclus(etat, lots, ids) {
  const ok = new Set(lotsVisibles(lots, etat).filter(estImprimable).map((l) => l.lot_id));
  return { ...etat, inclus: [...new Set(ids)].filter((id) => ok.has(id)) };
}

/** + / − on a lot: one more / one less label, within the bounds; a step on an excluded lot includes it. */
export function pas(etat, lots, lotId, delta) {
  const lot = lots.find((l) => l.lot_id === lotId);
  if (!estImprimable(lot)) return etat;
  const copies = borne((etat.copies[lotId] ?? 0) + delta);
  const inclus = delta > 0 && !etat.inclus.includes(lotId) ? [...etat.inclus, lotId] : etat.inclus;
  return { ...etat, copies: { ...etat.copies, [lotId]: copies }, inclus };
}

/**
 * A typed number of labels. Returns `{ etat, erreur }`: an integer outside 0…999 or a non-integer
 * is refused with M-S3-21 and the state is unchanged.
 */
export function saisirCopies(etat, lotId, texte) {
  const n = normalizeCopies(String(texte ?? "").trim() === "" ? "" : String(texte).trim());
  if (n === null) return { etat, erreur: PRINT_MESSAGES.copiesInvalides };
  return { etat: { ...etat, copies: { ...etat.copies, [lotId]: n } }, erreur: null };
}

/** The lots that print now: visible, printable, included, with copies > 0 — in list order. */
export function selection(lots, etat) {
  const inclus = new Set(etat.inclus);
  return lotsVisibles(lots, etat)
    .filter((l) => estImprimable(l) && inclus.has(l.lot_id) && (etat.copies[l.lot_id] ?? 0) > 0)
    .map((l) => ({ lot: l, copies: etat.copies[l.lot_id] }));
}

/** The LabelSheet input (components/print/LabelSheet.tsx LabelInputLike). */
export function etiquettesAImprimer(lots, etat) {
  return selection(lots, etat).map(({ lot, copies }) => ({
    key: lot.lot_id,
    code: lot.code,
    prix: lot.prix,
    copies,
    produit: {
      nom: lot.produit?.nom ?? "",
      dosage: lot.produit?.dosage ?? null,
      forme: lot.produit?.forme ?? null,
      conditionnement: lot.produit?.conditionnement ?? null,
    },
  }));
}

/**
 * The preview input: every visible printable lot, whatever its inclusion or count, so the active
 * line's label can be checked before it is ticked (ux/02 §6.7 « Aperçu »).
 */
export function etiquettesApercu(lots, etat) {
  return lotsVisibles(lots, etat)
    .filter(estImprimable)
    .map((lot) => ({
      key: lot.lot_id,
      code: lot.code,
      prix: lot.prix,
      copies: 1,
      produit: {
        nom: lot.produit?.nom ?? "",
        dosage: lot.produit?.dosage ?? null,
        forme: lot.produit?.forme ?? null,
        conditionnement: lot.produit?.conditionnement ?? null,
      },
    }));
}

/** The footer: number of labels and lots, and why printing is refused (M-S3-20), if it is. */
export function resume(lots, etat) {
  const s = selection(lots, etat);
  const etiquettes = s.reduce((t, x) => t + x.copies, 0);
  return { etiquettes, lots: s.length, refus: etiquettes === 0 ? PRINT_MESSAGES.aucuneEtiquette : null };
}

/**
 * The print-audit body of `POST /etiquettes-lots/impressions`, once the print dialog opened:
 * the lots that printed (visible, included, copies > 0).
 */
export function corpsImpression({ source, factureId = null }, lots, etat) {
  return {
    source,
    ...(source === SOURCE_ETIQUETTES.FACTURE && { facture_achat_id: factureId }),
    format: etat.format,
    lots: selection(lots, etat).map(({ lot, copies }) => ({ lot_id: lot.lot_id, copies })),
  };
}

/** The counters after a recorded print (the server's answer). */
export function apresImpression(etat, reponse) {
  const imprimees = { ...etat.imprimees };
  for (const l of reponse?.lots ?? []) {
    if (l.etiquettes_imprimees != null) imprimees[l.lot_id] = l.etiquettes_imprimees;
  }
  return { ...etat, imprimees };
}

/** Text of a lot's expiry in the list: MM/AAAA, « — » for the no-expiry sentinel. */
export function texteMoisPeremption(lot, vide = "—") {
  if (!lot?.date_peremption || lot.sans_peremption) return vide;
  const d = new Date(lot.date_peremption);
  if (Number.isNaN(d.getTime())) return vide;
  return `${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
}
